import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import DealModel, { DealAccountModel, DealDataConverter, DealVehicleModel } from "../../viewModels/Deal/DealModel";
import PaymentModel from "../../viewModels/Payments/PaymentModel";
import SortObject from "../../types/SortObject";
import { PaymentsFiltersType } from "../../components/V2Components/PaymentFilters";
import AccountPaymentModel, { AccountPaymentCreateType } from "../../viewModels/Deal/AccountPaymentModel";
import { AccountClosingDocumentCreateType, AccountClosingDocumentUploadType } from "../../viewModels/Deal/AccountClosingDocumentModel";
import type { GenerateDeliveryRequestDocumentInputModel } from "../../components/GenerateDeliveryRequestDocumentModal";
import type { GenerateAccountDocumentInputModel } from "../../components/GenerateAccountDocumentModal";
import AccountHistoryModel from "../../viewModels/Deal/AccountHistoryModel";
import DealHistoryModel from "../../viewModels/Deal/DealHistoryModel";
import type { GeneratePurchaseRequestDocumentInputModel } from "../../components/GeneratePurchaseRequestDocumentModal";
import TrackingSensorModel from "../../viewModels/Tracking/TrackingSensorModel";
import { CreateCargoInputForm, EditCargoInputForm } from "./components/Configuration/Cargo/components/types";
import { EditSegmentsInputForm, SegmentsSaveStatus } from "./components/Configuration/Segments/components/types";
import {
  EditTransportInputForm,
  EditVehiclesInputForm,
  TransportCargoDistributionInputForm,
  TransportSaveStatus,
} from "./components/Configuration/Transport/components/types";
import { BitrixCompanyModel } from "../../viewModels/Dictionaries/BitrixCompanyModel";
import { DealRoutesDto } from "../../dto/Deal/DealDto";
import type {
  CreateDealTransportationStagePlanRequestDto,
  UpdateDealTransportationStagePlanRequestDto,
} from "../../api/apiServices/backOffice/controllers/dealTransportationStagePlans/dealTransportationStagePlans.requestsDto";
import type { CopyVehicleSensorNumberInput } from "./components/Tracking/sensorCopy";
import BorderCrossingModel from "../../viewModels/Location/BorderCrossingModel";
import { getBorderCrossingRouteKey } from "./borderCrossingCache";
import type { DealTransportationStagePlanModel } from "../../viewModels/Deal/DealModel";
import type { ReviewSnapshot } from "../../components/CarrierReviews/reducer";
import { getCarrierReviewSnapshot } from "./utils/carrierReviewSnapshot";

const initialState: {
  loading: boolean;
  loadingData: boolean;
  routesLoadingData: boolean;
  applySensorNumberToAllPending: boolean;
  copyVehicleSensorNumberPending?: CopyVehicleSensorNumberInput;
  movizorRetryVehicleId?: number;
  transportationStagePlansLoading: boolean;
  copyTransportationStagePlanSource?: DealTransportationStagePlanModel;
  copyTransportationStagePlanStatus: "idle" | "pending" | "error";
  currentDealId?: number;
  routesUpdateDate?: string;
  dealRoutes?: DealRoutesDto;
  transportSaveStatus: TransportSaveStatus;
  segmentsSaveStatus: SegmentsSaveStatus;
  borderCrossingsByRoute: Record<string, Array<BorderCrossingModel>>;
  borderCrossingsLoadingByRoute: Record<string, boolean>;

  deal: DealModel;
  carrierReviewSnapshot?: ReviewSnapshot;
  paymentsInAccounts?: Array<PaymentModel>;

  selectedAccountId?: number;

  attachPaymentModalOpen: boolean;
  paymentsLoading: boolean;
  paymentsLoadingData: boolean;
  paymentsPage: number;
  paymentsPageSize: number;
  payments?: Array<PaymentModel>;
  paymentsTotal?: number;
  paymentsSort?: SortObject;
  paymentsFilters?: PaymentsFiltersType;
  selectedPaymentId?: number;
  attachPaymentLoading: boolean;

  createClosingDocumentModalOpen: boolean;
  createClosingDocumentModalLoading: boolean;
  createClosingDocumentsAccount?: DealAccountModel;

  deleteClosingDocumentModalOpen: boolean;
  deleteClosingDocumentModalLoading: boolean;
  deleteClosingDocumentsAccount?: DealAccountModel;

  generateDeliveryRequestDocumentModalOpen: boolean;
  generateDeliveryRequestDocumentModalLoading: boolean;

  generatePurchaseRequestDocumentModalOpen: boolean;
  generatePurchaseRequestDocumentModalLoading: boolean;

  generateAccountDocumentModalOpen: boolean;
  generateAccountDocumentModalLoading: boolean;
  accountIdToGenerateAccountDocument?: number;

  accountHistoryModalOpen: boolean;
  accountHistoryModalLoading: boolean;
  accountHistoryModalLoadingData: boolean;
  accountIdToShowHistory?: number;
  accountHistoryPage: number;
  accountHistoryPageSize: number;
  accountHistories?: Array<AccountHistoryModel>;
  accountHistoriesTotal?: number;

  dealHistoryModalOpen: boolean;
  dealHistoryModalLoading: boolean;
  dealHistoryModalLoadingData: boolean;
  dealHistoryPage: number;
  dealHistoryPageSize: number;
  dealHistories?: Array<DealHistoryModel>;
  dealHistoriesTotal?: number;
} = {
  loading: true,
  loadingData: true,
  routesLoadingData: false,
  applySensorNumberToAllPending: false,
  transportSaveStatus: "idle",
  segmentsSaveStatus: "idle",
  transportationStagePlansLoading: false,
  copyTransportationStagePlanStatus: "idle",
  borderCrossingsByRoute: {},
  borderCrossingsLoadingByRoute: {},

  deal: undefined!,

  attachPaymentModalOpen: false,
  paymentsLoading: true,
  paymentsLoadingData: true,
  paymentsPage: undefined!,
  paymentsPageSize: 10,
  attachPaymentLoading: false,

  createClosingDocumentModalOpen: false,
  createClosingDocumentModalLoading: false,

  deleteClosingDocumentModalOpen: false,
  deleteClosingDocumentModalLoading: false,

  generateDeliveryRequestDocumentModalOpen: false,
  generateDeliveryRequestDocumentModalLoading: false,

  generatePurchaseRequestDocumentModalLoading: false,
  generatePurchaseRequestDocumentModalOpen: false,

  generateAccountDocumentModalOpen: false,
  generateAccountDocumentModalLoading: false,

  accountHistoryModalLoading: false,
  accountHistoryModalLoadingData: false,
  accountHistoryModalOpen: false,
  accountHistoryPage: 1,
  accountHistoryPageSize: 10,

  dealHistoryModalLoading: false,
  dealHistoryModalLoadingData: false,
  dealHistoryModalOpen: false,
  dealHistoryPage: 1,
  dealHistoryPageSize: 10,
};

const dealSlice = createSlice({
  name: "deal",
  initialState,
  reducers: {
    loadDeal(state, action: PayloadAction<number>) {
      state.segmentsSaveStatus = "idle";
      state.loadingData = true;
      state.currentDealId = action.payload;
      state.routesLoadingData = false;
      state.routesUpdateDate = undefined;
      state.dealRoutes = undefined;
      state.borderCrossingsByRoute = {};
      state.borderCrossingsLoadingByRoute = {};
    },
    loadDealSuccess(
      state,
      action: PayloadAction<{
        deal: DealModel;
        payments: Array<PaymentModel>;
      }>,
    ) {
      const { deal, payments } = action.payload;
      state.loading = false;
      state.loadingData = false;
      const dealRoutes = state.dealRoutes?.dealId === deal.id ? state.dealRoutes : undefined;
      state.deal = dealRoutes ? DealDataConverter.applyDealRoutes(deal, dealRoutes) : deal;
      state.carrierReviewSnapshot = getCarrierReviewSnapshot(state.deal);
      state.paymentsInAccounts = payments;
    },
    loadDealRoutes(state, _action: PayloadAction<number>) {
      state.routesLoadingData = true;
    },
    loadDealRoutesSuccess(state, action: PayloadAction<DealRoutesDto>) {
      if (state.currentDealId !== action.payload.dealId) {
        return;
      }
      state.routesLoadingData = false;
      state.routesUpdateDate = action.payload.updateDate;
      state.dealRoutes = action.payload;
      if (state.deal?.id === action.payload.dealId) {
        state.deal = DealDataConverter.applyDealRoutes(state.deal, action.payload);
      }
    },
    loadDealRoutesReject(state, action: PayloadAction<number>) {
      if (state.currentDealId !== action.payload) {
        return;
      }
      state.routesLoadingData = false;
    },
    changeDeal(state, action: PayloadAction<DealModel>) {
      state.deal = action.payload;
    },

    resetState: () => initialState,

    updateDeal(state) {
      state.loadingData = true;
    },
    updateDealSuccess(state, action: PayloadAction<DealModel>) {
      state.loadingData = false;
      const dealWithPreservedRoutes = DealDataConverter.preserveDealRoutes(action.payload, state.deal);
      dealWithPreservedRoutes.observerOnly = action.payload.observerOnly ?? state.deal?.observerOnly;
      const dealRoutes = state.dealRoutes?.dealId === dealWithPreservedRoutes.id ? state.dealRoutes : undefined;
      state.deal = dealRoutes ? DealDataConverter.applyDealRoutes(dealWithPreservedRoutes, dealRoutes) : dealWithPreservedRoutes;
      // Reviews must observe server-confirmed changes, not optimistic edits from changeDeal.
      state.carrierReviewSnapshot = getCarrierReviewSnapshot(state.deal);

      // обновление из ЛП, поэтому тут
      state.deleteClosingDocumentModalLoading = false;
      state.deleteClosingDocumentModalOpen = false;

      state.attachPaymentLoading = false;
      state.attachPaymentModalOpen = false;
      state.payments = undefined;
      state.paymentsTotal = undefined;
      state.selectedAccountId = undefined;
      state.paymentsFilters = undefined;
      state.selectedPaymentId = undefined;

      state.createClosingDocumentModalLoading = false;
      state.createClosingDocumentModalOpen = false;
      state.createClosingDocumentsAccount = undefined;
      state.transportationStagePlansLoading = false;
    },
    updateDealReject(state) {
      state.loadingData = false;
    },

    createTransportationStagePlan(state, _action: PayloadAction<{ dealId: number; dto: CreateDealTransportationStagePlanRequestDto }>) {
      state.transportationStagePlansLoading = true;
    },
    openCopyTransportationStagePlan(state, action: PayloadAction<DealTransportationStagePlanModel>) {
      if (state.copyTransportationStagePlanStatus === "pending") return;
      state.copyTransportationStagePlanSource = action.payload;
      state.copyTransportationStagePlanStatus = "idle";
    },
    closeCopyTransportationStagePlan(state) {
      if (state.copyTransportationStagePlanStatus === "pending") return;
      state.copyTransportationStagePlanSource = undefined;
      state.copyTransportationStagePlanStatus = "idle";
    },
    copyTransportationStagePlan(
      state,
      _action: PayloadAction<{
        dealId: number;
        plan: DealTransportationStagePlanModel;
        dealVehicleIds: Array<number>;
        copyPlannedDates: boolean;
        copyActualDates: boolean;
      }>,
    ) {
      state.copyTransportationStagePlanStatus = "pending";
    },
    copyTransportationStagePlanSuccess(state) {
      state.copyTransportationStagePlanSource = undefined;
      state.copyTransportationStagePlanStatus = "idle";
    },
    copyTransportationStagePlanReject(state) {
      state.copyTransportationStagePlanStatus = "error";
    },
    createTransportationStagePlanSuccess(state) {
      state.transportationStagePlansLoading = false;
    },
    createTransportationStagePlanReject(state) {
      state.transportationStagePlansLoading = false;
    },
    updateTransportationStagePlan(
      state,
      _action: PayloadAction<{ dealId: number; planId: number; dto: UpdateDealTransportationStagePlanRequestDto }>,
    ) {
      state.transportationStagePlansLoading = true;
    },
    updateTransportationStagePlanSuccess(state) {
      state.transportationStagePlansLoading = false;
    },
    updateTransportationStagePlanReject(state) {
      state.transportationStagePlansLoading = false;
    },
    applyTransportationStagePlanHints(
      state,
      _action: PayloadAction<{
        dealId: number;
        planIds: Array<number>;
      }>,
    ) {
      state.transportationStagePlansLoading = true;
    },
    applyTransportationStagePlanHintsSuccess(state) {
      state.transportationStagePlansLoading = false;
    },
    applyTransportationStagePlanHintsReject(state) {
      state.transportationStagePlansLoading = false;
    },
    deleteTransportationStagePlan(state, _action: PayloadAction<{ dealId: number; planId: number }>) {
      state.transportationStagePlansLoading = true;
    },
    deleteTransportationStagePlanSuccess(state) {
      state.transportationStagePlansLoading = false;
    },
    deleteTransportationStagePlanReject(state) {
      state.transportationStagePlansLoading = false;
    },

    // сформировать бумажную версию заявки на доставку

    generateDeliveryRequestDocument(state, _action: PayloadAction<GenerateDeliveryRequestDocumentInputModel>) {
      state.generateDeliveryRequestDocumentModalLoading = true;
    },
    generateDeliveryRequestDocumentSuccess(state) {
      state.generateDeliveryRequestDocumentModalLoading = false;
      state.generateDeliveryRequestDocumentModalOpen = false;
    },
    generateDeliveryRequestDocumentReject(state) {
      state.generateDeliveryRequestDocumentModalLoading = false;
    },

    // сформировать бумажную версию счета
    generateAccountDocument(state, _action: PayloadAction<GenerateAccountDocumentInputModel>) {
      state.generateAccountDocumentModalLoading = true;
    },
    generateAccountDocumentSuccess(state) {
      state.generateAccountDocumentModalLoading = false;
      state.generateAccountDocumentModalOpen = false;
      state.accountIdToGenerateAccountDocument = undefined;
    },
    generateAccountDocumentReject(state) {
      state.generateAccountDocumentModalLoading = false;
    },

    openAttachPaymentModal(state, action: PayloadAction<{ filters: PaymentsFiltersType; accountId: number }>) {
      state.attachPaymentModalOpen = true;
      state.paymentsLoading = true;
      state.paymentsLoadingData = true;
      state.paymentsPage = 1;
      state.paymentsFilters = action.payload.filters;
      state.selectedAccountId = action.payload.accountId;
    },

    closeAttachPaymentModal(state) {
      if (!state.attachPaymentLoading) {
        state.attachPaymentModalOpen = false;
        state.payments = undefined;
        state.paymentsTotal = undefined;
        state.selectedAccountId = undefined;
        state.paymentsFilters = undefined;
        state.selectedPaymentId = undefined;
      }
    },

    loadPaymentsSuccess(state, action: PayloadAction<{ payments: Array<PaymentModel>; total: number }>) {
      const { payments, total } = action.payload;

      state.paymentsLoading = false;
      state.paymentsLoadingData = false;
      state.payments = payments;
      state.paymentsTotal = total;
    },

    changePaymentsTable(state, action: PayloadAction<{ page: number; pageSize: number; sort?: SortObject; filters: PaymentsFiltersType }>) {
      const { page, pageSize, sort, filters } = action.payload;
      state.paymentsLoadingData = true;
      state.paymentsPage = page;
      state.paymentsPageSize = pageSize;
      state.paymentsSort = sort;
      state.paymentsFilters = filters;
      state.selectedPaymentId = undefined;
    },

    changeSelectedPaymentId(state, action: PayloadAction<number | undefined>) {
      state.selectedPaymentId = action.payload;
    },

    attachPayment(state, _action: PayloadAction<AccountPaymentCreateType>) {
      state.attachPaymentLoading = true;
    },

    deleteAccountPayment(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },

    editAccountPayment(_state, _action: PayloadAction<AccountPaymentModel>) {},

    generateAccountClosingDocuments(state, _action: PayloadAction<Array<AccountClosingDocumentCreateType>>) {
      state.loadingData = true;
    },
    generateAccountClosingDocumentsSuccess(state) {
      state.loadingData = false;

      state.createClosingDocumentModalLoading = false;
      state.createClosingDocumentModalOpen = false;
      state.createClosingDocumentsAccount = undefined;
    },
    generateAccountClosingDocumentsReject(state) {
      state.loadingData = false;
    },

    deleteAccountClosingDocuments(state, _action: PayloadAction<{ accountId: number; ids: Array<number> }>) {
      state.deleteClosingDocumentModalLoading = true;
    },

    uploadAccountClosingDocument(_state, _action: PayloadAction<Array<AccountClosingDocumentUploadType>>) {},

    openCreateClosingDocumentModal(state, action: PayloadAction<DealAccountModel>) {
      state.loadingData = true;
      state.createClosingDocumentsAccount = action.payload;
    },
    openCreateClosingDocumentModalSuccess(state) {
      state.loadingData = false;
      state.createClosingDocumentModalOpen = true;
    },
    openCreateClosingDocumentModalReject(state) {
      state.loadingData = false;
      state.createClosingDocumentsAccount = undefined;
    },

    closeCreateClosingDocumentModal(state) {
      state.createClosingDocumentModalOpen = false;
      state.createClosingDocumentsAccount = undefined;
    },

    openDeleteClosingDocumentModal(state, action: PayloadAction<DealAccountModel>) {
      state.deleteClosingDocumentModalOpen = true;
      state.deleteClosingDocumentsAccount = action.payload;
    },

    closeDeleteClosingDocumentModal(state) {
      state.deleteClosingDocumentModalOpen = false;
      state.deleteClosingDocumentsAccount = undefined;
    },

    updateClient(state, _action: PayloadAction<BitrixCompanyModel>) {
      state.loadingData = true;
    },

    updateClientReject(state) {
      state.loadingData = false;
    },

    updateContract(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },

    startLongPoll() {},
    stopLongPoll() {},
    startRoutesLongPoll(_state, _action: PayloadAction<number>) {},
    stopRoutesLongPoll() {},

    openGenerateDeliveryRequestDocumentModal(state) {
      state.generateDeliveryRequestDocumentModalOpen = true;
    },

    closeGenerateDeliveryRequestDocumentModal(state) {
      state.generateDeliveryRequestDocumentModalOpen = false;
    },

    openGenerateAccountDocumentModal(state, action: PayloadAction<number>) {
      state.generateAccountDocumentModalOpen = true;
      state.accountIdToGenerateAccountDocument = action.payload;
    },

    closeGenerateAccountDocumentModal(state) {
      state.generateAccountDocumentModalOpen = false;
      state.accountIdToGenerateAccountDocument = undefined;
    },

    openAccountHistoryModal(state, action: PayloadAction<number>) {
      state.accountHistoryModalOpen = true;
      state.accountHistoryModalLoading = true;
      state.accountHistoryModalLoadingData = true;
      state.accountIdToShowHistory = action.payload;
      state.accountHistoryPage = 1;
    },

    closeAccountHistoryModal(state) {
      state.accountHistoryModalOpen = false;
      state.accountHistoryModalLoading = false;
      state.accountHistoryModalLoadingData = false;
      state.accountIdToShowHistory = undefined;
      state.accountHistoriesTotal = undefined;
    },

    changeAccountHistoryTable(state, action: PayloadAction<{ page: number; pageSize: number }>) {
      state.accountHistoryModalLoadingData = true;
      state.accountHistoryPage = action.payload.page;
      state.accountHistoryPageSize = action.payload.pageSize;
    },

    loadAccountHistorySuccess(state, action: PayloadAction<{ accountHistories: Array<AccountHistoryModel>; total: number }>) {
      state.accountHistoryModalLoading = false;
      state.accountHistoryModalLoadingData = false;
      state.accountHistories = action.payload.accountHistories;
      state.accountHistoriesTotal = action.payload.total;
    },

    openDealHistoryModal(state) {
      state.dealHistoryModalOpen = true;
      state.dealHistoryModalLoading = true;
      state.dealHistoryModalLoadingData = true;
      state.dealHistoryPage = 1;
    },

    closeDealHistoryModal(state) {
      state.dealHistoryModalOpen = false;
      state.dealHistoryModalLoading = false;
      state.dealHistoryModalLoadingData = false;
      state.dealHistoriesTotal = undefined;
    },

    changeDealHistoryTable(state, action: PayloadAction<{ page: number; pageSize: number }>) {
      state.dealHistoryModalLoadingData = true;
      state.dealHistoryPage = action.payload.page;
      state.dealHistoryPageSize = action.payload.pageSize;
    },

    loadDealHistorySuccess(state, action: PayloadAction<{ dealHistories: Array<DealHistoryModel>; total: number }>) {
      state.dealHistoryModalLoading = false;
      state.dealHistoryModalLoadingData = false;
      state.dealHistories = action.payload.dealHistories;
      state.dealHistoriesTotal = action.payload.total;
    },

    openGeneratePurchaseRequestDocumentModal(state) {
      state.generatePurchaseRequestDocumentModalOpen = true;
    },

    closeGeneratePurchaseRequestDocumentModal(state) {
      state.generatePurchaseRequestDocumentModalOpen = false;
    },

    // сформировать бумажную версию заявки на доставку

    generatePurchaseRequestDocument(state, _action: PayloadAction<GeneratePurchaseRequestDocumentInputModel>) {
      state.generatePurchaseRequestDocumentModalLoading = true;
    },
    generatePurchaseRequestDocumentSuccess(state) {
      state.generatePurchaseRequestDocumentModalLoading = false;
      state.generatePurchaseRequestDocumentModalOpen = false;
    },
    generatePurchaseRequestDocumentReject(state) {
      state.generatePurchaseRequestDocumentModalLoading = false;
    },

    createCargo(state, _action: PayloadAction<CreateCargoInputForm>) {
      state.loadingData = true;
    },
    createCargoReject(state) {
      state.loadingData = false;
    },

    updateCargo(state, _action: PayloadAction<EditCargoInputForm>) {
      state.loadingData = true;
    },
    updateCargoReject(state) {
      state.loadingData = false;
    },

    replaceSegments(state, _action: PayloadAction<EditSegmentsInputForm>) {
      state.loadingData = true;
      state.segmentsSaveStatus = "pending";
    },
    replaceSegmentsSuccess(state, action: PayloadAction<number>) {
      if (state.currentDealId !== action.payload) return;
      state.loadingData = false;
      state.segmentsSaveStatus = "success";
    },
    replaceSegmentsReject(state, action: PayloadAction<number>) {
      if (state.currentDealId !== action.payload) return;
      state.loadingData = false;
      state.segmentsSaveStatus = "error";
    },
    resetSegmentsSaveStatus(state) {
      state.segmentsSaveStatus = "idle";
    },
    loadBorderCrossings(state, action: PayloadAction<{ from: number; to: number }>) {
      state.borderCrossingsLoadingByRoute[getBorderCrossingRouteKey(action.payload.from, action.payload.to)] = true;
    },
    loadBorderCrossingsSuccess(state, action: PayloadAction<{ key: string; borderCrossings: Array<BorderCrossingModel> }>) {
      state.borderCrossingsByRoute[action.payload.key] = action.payload.borderCrossings;
      state.borderCrossingsLoadingByRoute[action.payload.key] = false;
    },
    loadBorderCrossingsReject(state, action: PayloadAction<{ key: string }>) {
      state.borderCrossingsLoadingByRoute[action.payload.key] = false;
    },
    replaceTransport(state, _action: PayloadAction<EditTransportInputForm>) {
      state.loadingData = true;
      state.transportSaveStatus = "pending";
    },
    saveVehicle(state, _action: PayloadAction<{ vehicleId: number; vehicleIds?: Array<number>; form: EditVehiclesInputForm }>) {
      state.loadingData = true;
    },
    saveVehicleSuccess(state) {
      state.loadingData = false;
    },
    saveVehicleReject(state) {
      state.loadingData = false;
    },
    replaceTransportSuccess(
      state,
      action: PayloadAction<{
        vehicles: Array<DealVehicleModel>;
        segmentCarrierSettings: EditTransportInputForm["segmentCarrierSettings"];
      }>,
    ) {
      state.loadingData = false;
      state.transportSaveStatus = "success";
      state.deal.vehicles = action.payload.vehicles;
      action.payload.segmentCarrierSettings.forEach((setting) => {
        const segment = state.deal.segments.find((item) => item.id === setting.dealSegmentId);
        if (segment) {
          segment.carrierMode = setting.carrierMode;
        }
      });
    },
    replaceTransportReject(state, action: PayloadAction<"vehiclesError" | "segmentsError">) {
      state.loadingData = false;
      state.transportSaveStatus = action.payload;
    },
    resetTransportSaveStatus(state) {
      state.transportSaveStatus = "idle";
    },
    distributeCargo(state, _action: PayloadAction<TransportCargoDistributionInputForm>) {
      state.loadingData = true;
    },
    distributeCargoReject(state) {
      state.loadingData = false;
    },
    linkVehicleSensor(
      state,
      _action: PayloadAction<{ vehicleId: number; trackingObjectId: string; trackingObject?: TrackingSensorModel }>,
    ) {
      state.loadingData = true;
    },
    unlinkVehicleSensor(state, _action: PayloadAction<number>) {
      state.loadingData = true;
    },
    retryMovizor(state, action: PayloadAction<number>) {
      if (!state.movizorRetryVehicleId) {
        state.movizorRetryVehicleId = action.payload;
      }
    },
    retryMovizorSuccess(state) {
      state.movizorRetryVehicleId = undefined;
    },
    retryMovizorReject(state) {
      state.movizorRetryVehicleId = undefined;
    },
    applySensorNumberToAll(state, _action: PayloadAction<string>) {
      state.applySensorNumberToAllPending = true;
    },
    applySensorNumberToAllSuccess(state) {
      state.applySensorNumberToAllPending = false;
    },
    applySensorNumberToAllReject(state) {
      state.applySensorNumberToAllPending = false;
    },
    copyVehicleSensorNumber(state, action: PayloadAction<CopyVehicleSensorNumberInput>) {
      if (!state.copyVehicleSensorNumberPending && !state.loadingData && !state.applySensorNumberToAllPending) {
        state.copyVehicleSensorNumberPending = action.payload;
      }
    },
    copyVehicleSensorNumberSuccess(state) {
      state.copyVehicleSensorNumberPending = undefined;
    },
    copyVehicleSensorNumberReject(state) {
      state.copyVehicleSensorNumberPending = undefined;
    },
    updateVehicleSensorSuccess(state, action: PayloadAction<{ vehicle: DealVehicleModel }>) {
      state.loadingData = false;
      const vehicleIndex = state.deal.vehicles.findIndex((vehicle) => vehicle.id === action.payload.vehicle.id);

      if (vehicleIndex !== -1) {
        const previousTrackingObjectId = state.deal.vehicles[vehicleIndex].trackingObjectId;
        const trackingObject = action.payload.vehicle.trackingObject;
        state.deal.vehicles[vehicleIndex] = action.payload.vehicle;

        if (trackingObject?.id) {
          const trackingObjects = state.deal.trackingObjects ?? [];
          const trackingObjectIndex = trackingObjects.findIndex((item) => item.id === trackingObject.id);

          if (trackingObjectIndex === -1) {
            trackingObjects.push(trackingObject);
          } else {
            trackingObjects[trackingObjectIndex] = trackingObject;
          }

          state.deal.trackingObjects = trackingObjects;
        }

        if (previousTrackingObjectId && previousTrackingObjectId !== action.payload.vehicle.trackingObjectId) {
          const isStillUsed = state.deal.vehicles.some((vehicle) => vehicle.trackingObjectId === previousTrackingObjectId);
          if (!isStillUsed) {
            state.deal.trackingObjects = (state.deal.trackingObjects ?? []).filter(
              (trackingObject) => trackingObject.id !== previousTrackingObjectId,
            );
          }
        }
      }
    },
    updateVehicleSensorReject(state) {
      state.loadingData = false;
    },
  },
});

export default dealSlice;
